import { ChangeDetectionStrategy, Component, computed, ElementRef, input, signal, viewChild } from '@angular/core';

let nextTipId = 0;

const TIP_WIDTH = 260;
const VIEWPORT_GUTTER = 12;
@Component({
  selector: '[appTotalsFigure]',
  standalone: false,
  template: `
    <dt class="totals-figure__label">
      {{ label() }}
      @if (info()) {
        <button
          #infoBtn
          type="button"
          class="totals-figure__info"
          [attr.aria-label]="'About ' + label()"
          [attr.aria-describedby]="tipPos() ? tipId : null"
          (mouseenter)="showTip()"
          (mouseleave)="hideTip()"
          (focus)="showTip()"
          (blur)="hideTip()"
          (keydown.escape)="hideTip()"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 7.25v3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <circle cx="8" cy="5.1" r="0.9" fill="currentColor"/>
          </svg>
        </button>
        @if (tipPos(); as pos) {
          <span class="totals-figure__tip" role="tooltip" [id]="tipId" [style.top.px]="pos.top" [style.left.px]="pos.left">{{ info() }}</span>
        }
      }
    </dt>
    <dd class="totals-figure__value"><ng-content /></dd>
    @if (note()) {
      <dd class="totals-figure__note">{{ note() }}</dd>
    }
    @if (progressPct() !== null) {
      <dd class="totals-figure__bar" role="img" [attr.aria-label]="progressPct() + '%'">
        <span class="totals-figure__bar-fill" [style.width.%]="progressPct()"></span>
      </dd>
    }
  `,
  styleUrl: './admin-totals-figure.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'totals-figure',
    '[class.totals-figure--danger]': 'tone() === "danger"',
  },
})
export class AdminTotalsFigure {
  readonly label = input.required<string>();
  readonly note = input<string>('');
  /** What the figure counts, shown in a tooltip from the ⓘ. */
  readonly info = input<string>('');
  readonly tone = input<'default' | 'danger'>('default');
  /** 0–1; omit for no bar. */
  readonly progress = input<number | null>(null);

  protected readonly tipId = `totals-tip-${nextTipId++}`;
  /** Fixed-position coordinates, so the card's overflow:hidden (for its
   *  rounded corners) can't clip the tooltip; null = hidden. */
  protected readonly tipPos = signal<{ top: number; left: number } | null>(null);

  private readonly infoBtn = viewChild<ElementRef<HTMLButtonElement>>('infoBtn');

  protected readonly progressPct = computed(() => {
    const p = this.progress();
    return p === null ? null : Math.round(Math.min(1, Math.max(0, p)) * 100);
  });

  protected showTip(): void {
    const btn = this.infoBtn()?.nativeElement;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Below the icon, nudged left so it stays on screen in the last column.
    const maxLeft = window.innerWidth - TIP_WIDTH - VIEWPORT_GUTTER;
    this.tipPos.set({
      top: rect.bottom + 8,
      left: Math.max(VIEWPORT_GUTTER, Math.min(rect.left - 12, maxLeft)),
    });
  }

  protected hideTip(): void {
    this.tipPos.set(null);
  }
}
